import { useContext, useMemo, useState } from 'react';
import { Dropdown as AntDropdown } from 'antd';
import styled from 'styled-components';
import FeatherIcon from 'feather-icons-react';
import Icon from 'components/icons';
import Notification from 'components/notification';
import { TeamsContext } from 'contexts/teams';
import { SessionContext } from 'contexts/session';
import { ROLES } from 'constants/roles';
import { getTeamId } from 'utils/teams';
import { ContentAccessMode } from 'contexts/teams/types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { batchUpdateTeamContentAccess } from 'services/teams';
import { ContentAccessRadio } from '../TeamSharedStyles';

const UNRESTRICTED_CONTENT_ACCESS_MODE: ContentAccessMode = 'unrestricted';
const ASSIGNED_CONTENT_ONLY_MODE: ContentAccessMode = 'assigned_content_only';
const BULK_CONTENT_ACCESS_RADIO_NAME = 'bulk-content-access';

const TEAM_LABEL = 'team';
const TEAMS_LABEL = 'teams';
const SELECTED_LABEL = 'selected';
const CLEAR_SELECTION_LABEL = 'Clear selection';
const CONTENT_ACCESS_LABEL = 'Content access';
const REMOVE_TEAMS_LABEL = 'Remove teams';
const UNLIMITED_ACCESS_LABEL = 'Unlimited access';
const ASSIGNED_CONTENT_ONLY_LABEL = 'Assigned content only';
const UNLIMITED_ACCESS_DESCRIPTION = 'Learners can browse and access the full training catalog.';
const ASSIGNED_CONTENT_ONLY_DESCRIPTION = 'Learners only see content assigned through playlists.';
const ASSIGNED_CONTENT_ONLY_WARNING =
  "If users belong to other teams with unlimited content access, they'll still have full access to all content.";
const SAVE_LABEL = 'Save';
const CONTENT_ACCESS_UPDATED_MESSAGE = 'Content access updated';
const UNMANAGED_TEAMS_ERROR_MESSAGE =
  "We couldn't update content access because one or more selected teams can't be managed by your account.";
const BULK_UPDATE_ERROR_MESSAGE =
  "We couldn't update content access for the selected teams. Please try again.";

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  width: 100%;
  /* Top-only margin: ContentTable already adds 20px/30px top padding right below
     this bar, so a bottom margin here would double the gap. 20px matches that
     rhythm on the top side too. Exact value subject to visual QA. */
  margin: 20px 0 0;
  padding: 10px 15px;
  border: 2px solid var(--lilac);
  border-radius: 7px;
  background: var(--fog-15);
`;

const SelectionGroup = styled.div`
  display: flex;
  flex: 1 0 0;
  align-items: center;
  gap: 15px;
  min-width: 0;
`;

const SelectionText = styled.span`
  font-size: 16px;
  font-weight: 900;
  line-height: 23px;
  color: var(--black-dark);
  white-space: nowrap;
`;

const ClearSelectionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 3px 8px;
  border: none;
  border-radius: 5px;
  background: var(--lilac-dark);
  color: var(--white);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  cursor: pointer;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 24px;
  border: 1px solid var(--lilac);
  border-radius: 8px;
  background: transparent;
  color: var(--black-dark);
  font-size: 16px;
  font-weight: 700;
  line-height: 23px;
  cursor: pointer;

  &:hover,
  &:focus,
  &:active {
    background: transparent;
    border: 1px solid var(--black-dark);
  }
`;

const CountPill = styled.span`
  padding: 6px 10px;
  border-radius: 30px;
  background: var(--lilac-dark);
  color: var(--white);
  font-size: 12px;
  font-weight: 400;
  line-height: 14px;
`;

const DropdownMenu = styled.div`
  width: 258px;
  padding: 12px;
  border-radius: 10px;
  background: var(--white);
  box-shadow: 0px 30px 24px rgba(0, 0, 0, 0.2);
`;

const OptionCard = styled.label<{ $selected: boolean }>`
  width: 234px;
  padding: 10px;
  border-radius: 7px;
  border: ${({ $selected }) =>
    $selected ? `2px solid var(--lilac)` : `1px solid var(--dusty-gray)`};
  background: ${({ $selected }) => ($selected ? 'var(--fog-15)' : 'var(--white)')};
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-bottom: 12px;
  cursor: pointer;
`;

const OptionTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: var(--black-dark);
`;

const OptionDescription = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  color: var(--light-gray-darker);
`;

const WarningDivider = styled.div`
  height: 1px;
  width: 100%;
  background: var(--lilac-lighter);
  margin: 8px 0;
`;

const SaveButton = styled.button`
  width: 234px;
  height: 36px;
  border: 0;
  border-radius: 8px;
  background: var(--black-dark);
  color: var(--white);
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

type ContentAccessUpdateResult = {
  team_id: string;
  content_access_mode: ContentAccessMode;
};

const teamCountLabel = (count: number): string =>
  `${count} ${count === 1 ? TEAM_LABEL : TEAMS_LABEL}`;

const bulkActionsLabel = (count: number): string =>
  `Bulk actions for ${teamCountLabel(count)} ${SELECTED_LABEL}`;

const contentAccessUpdatedMessage = (count: number): string => {
  return count === 1
    ? CONTENT_ACCESS_UPDATED_MESSAGE
    : `${CONTENT_ACCESS_UPDATED_MESSAGE} for ${teamCountLabel(count)}`;
};

const TeamsBulkActionBar = (): JSX.Element | null => {
  const {
    selectedItems,
    actions: teamsActions,
    table: { items: tableItems },
  } = useContext(TeamsContext);
  const { userRoleIds, teams, user, isAccountContentRestrictionsEnabled } =
    useContext(SessionContext);
  const { contentRestrictions } = useFlags() || {};
  const [open, setOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState<ContentAccessMode>(
    UNRESTRICTED_CONTENT_ACCESS_MODE
  );
  const [saving, setSaving] = useState(false);

  const isAccountOwner = Boolean(userRoleIds?.includes(ROLES.ACCOUNT_OWNER));
  const countRows = selectedItems?.length || 0;

  const isContentRestrictionsVisible =
    Boolean(contentRestrictions) && Boolean(isAccountContentRestrictionsEnabled);

  const manageableSelectedTeams = useMemo(() => {
    if (!isContentRestrictionsVisible || countRows === 0) {
      return [];
    }

    const selectedTeams = (tableItems || []).filter((item) =>
      selectedItems.includes(getTeamId(item))
    );

    return selectedTeams.filter((team) => {
      if (isAccountOwner) {
        return true;
      }

      const teamId = getTeamId(team);
      const managerTeams = user?.teams ?? teams ?? [];
      const managedTeam = managerTeams?.find(
        (sessionTeam) => sessionTeam.id === teamId || sessionTeam.team_id === teamId
      );
      return String(managedTeam?.is_manager) === 'true';
    });
  }, [
    isContentRestrictionsVisible,
    countRows,
    tableItems,
    selectedItems,
    isAccountOwner,
    teams,
    user?.teams,
  ]);

  const manageableSelectedTeamIds = useMemo(() => {
    return manageableSelectedTeams
      .map(getTeamId)
      .filter((teamId): teamId is string => Boolean(teamId));
  }, [manageableSelectedTeams]);

  const hasManageableSelectedTeam = manageableSelectedTeamIds.length > 0;

  if (countRows === 0) {
    return null;
  }

  const handleVisibleChange = (visible: boolean) => {
    setOpen(visible);
    if (!visible) {
      setSelectedMode(UNRESTRICTED_CONTENT_ACCESS_MODE);
    }
  };

  const handleBulkSave = async () => {
    if (!hasManageableSelectedTeam || saving) {
      return;
    }

    setSaving(true);

    try {
      const response = await batchUpdateTeamContentAccess({
        teams_ids: manageableSelectedTeamIds,
        content_access_mode: selectedMode,
      });
      const updatedTeams: ContentAccessUpdateResult[] = Array.isArray(response.data)
        ? response.data
        : [];
      const updatedTeamCount = Array.isArray(response.data)
        ? updatedTeams.length
        : manageableSelectedTeamIds.length;
      const updatedModeByTeamId = updatedTeams.reduce<Record<string, ContentAccessMode>>(
        (acc, team) => ({
          ...acc,
          [team.team_id]: team.content_access_mode,
        }),
        {}
      );

      teamsActions.onTableUpdate(
        (tableItems || []).map((item) => {
          const teamId = getTeamId(item);
          const contentAccessMode = teamId ? updatedModeByTeamId[teamId] : undefined;

          return contentAccessMode ? { ...item, content_access_mode: contentAccessMode } : item;
        })
      );
      teamsActions.onClearSelection();
      Notification({
        type: 'success',
        text: contentAccessUpdatedMessage(updatedTeamCount),
        singleLine: true,
      });
      setOpen(false);
    } catch (error) {
      const unmanagedTeamIds = error?.response?.data?.unmanaged_team_ids;
      Notification({
        type: 'error',
        text:
          error?.response?.status === 403 && unmanagedTeamIds?.length
            ? UNMANAGED_TEAMS_ERROR_MESSAGE
            : BULK_UPDATE_ERROR_MESSAGE,
        singleLine: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const contentAccessMenu = (
    <DropdownMenu>
      <OptionCard
        data-testid="bulk-content-access-option-unrestricted"
        $selected={selectedMode === UNRESTRICTED_CONTENT_ACCESS_MODE}
      >
        <ContentAccessRadio
          type="radio"
          name={BULK_CONTENT_ACCESS_RADIO_NAME}
          checked={selectedMode === UNRESTRICTED_CONTENT_ACCESS_MODE}
          onChange={() => setSelectedMode(UNRESTRICTED_CONTENT_ACCESS_MODE)}
        />
        <div>
          <OptionTitle>{UNLIMITED_ACCESS_LABEL}</OptionTitle>
          <OptionDescription>{UNLIMITED_ACCESS_DESCRIPTION}</OptionDescription>
        </div>
      </OptionCard>

      <OptionCard
        data-testid="bulk-content-access-option-assigned"
        $selected={selectedMode === ASSIGNED_CONTENT_ONLY_MODE}
      >
        <ContentAccessRadio
          type="radio"
          name={BULK_CONTENT_ACCESS_RADIO_NAME}
          checked={selectedMode === ASSIGNED_CONTENT_ONLY_MODE}
          onChange={() => setSelectedMode(ASSIGNED_CONTENT_ONLY_MODE)}
        />
        <div>
          <OptionTitle>{ASSIGNED_CONTENT_ONLY_LABEL}</OptionTitle>
          <OptionDescription>{ASSIGNED_CONTENT_ONLY_DESCRIPTION}</OptionDescription>
          {selectedMode === ASSIGNED_CONTENT_ONLY_MODE && (
            <>
              <WarningDivider />
              <OptionDescription>{ASSIGNED_CONTENT_ONLY_WARNING}</OptionDescription>
            </>
          )}
        </div>
      </OptionCard>

      <SaveButton
        data-testid="bulk-content-access-save"
        type="button"
        disabled={saving || !hasManageableSelectedTeam}
        onClick={handleBulkSave}
      >
        {SAVE_LABEL}
      </SaveButton>
    </DropdownMenu>
  );

  return (
    <Toolbar role="toolbar" aria-label={bulkActionsLabel(countRows)}>
      <SelectionGroup>
        <SelectionText>
          {teamCountLabel(countRows)} {SELECTED_LABEL}
        </SelectionText>
        <ClearSelectionButton type="button" onClick={teamsActions.onClearSelection}>
          <FeatherIcon icon="x" size={16} color="var(--white)" aria-hidden="true" />
          {CLEAR_SELECTION_LABEL}
        </ClearSelectionButton>
      </SelectionGroup>

      {hasManageableSelectedTeam && (
        <AntDropdown
          overlay={contentAccessMenu}
          trigger={['click']}
          visible={open}
          onVisibleChange={handleVisibleChange}
          placement="bottomRight"
        >
          <ActionButton type="button">
            <span>{CONTENT_ACCESS_LABEL}</span>
            <CountPill>{teamCountLabel(countRows)}</CountPill>
            <Icon
              icon="ChevronDown"
              color="var(--light-gray-darker)"
              style={{ width: '10px', height: '6px' }}
            />
          </ActionButton>
        </AntDropdown>
      )}

      <ActionButton type="button" onClick={() => teamsActions.onRemoveTeam(selectedItems)}>
        <span>{REMOVE_TEAMS_LABEL}</span>
        <CountPill>{teamCountLabel(countRows)}</CountPill>
      </ActionButton>
    </Toolbar>
  );
};

export default TeamsBulkActionBar;
